import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, SimpleTable, Typeahead, useToast } from '@src/components/ui';
import AdminLayout from '../AdminLayout';
import { createManufacturerShippingRate, deleteManufacturerShippingRate, fetchAvailableManufacturers, fetchManufacturerShippingRates, updateManufacturerShippingRate, } from '../../../api/ManufacturerShippingAdminApi';
import { createManufacturerShippingColumns } from './tableColumns';
import styles from './ManufacturerShipping.module.scss';
const toBooleanValue = (value) => {
    if (typeof value === 'boolean')
        return value;
    if (typeof value === 'number')
        return value === 1;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', '1', 'yes', 'y'].includes(normalized))
            return true;
        if (['false', '0', 'no', 'n', ''].includes(normalized))
            return false;
    }
    return false;
};
const toPriceValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};
const getRateErrorMessage = (error) => {
    const payload = error?.response?.body;
    const nonFieldMessage = payload?.non_field_errors?.[0] ?? payload?.detail;
    if (typeof nonFieldMessage === 'string' && nonFieldMessage.trim()) {
        return nonFieldMessage;
    }
    const fieldMessage = payload?.name?.[0] ?? payload?.shipping_amount?.[0];
    if (typeof fieldMessage === 'string' && fieldMessage.trim()) {
        return fieldMessage;
    }
    return 'Failed to save manufacturer shipping rate';
};
/**
 * A rate row is meaningless with a blank name or amount, so - unlike the product form's
 * validateShippingAmount, where blank means "fall back" - both fields are required here.
 */
const validatePayload = (name, amount) => {
    if (!name.trim())
        return 'Manufacturer name is required';
    if (!amount.trim())
        return 'Shipping amount is required';
    const parsed = Number(amount);
    if (!Number.isFinite(parsed))
        return 'Shipping amount must be a number';
    if (parsed < 0)
        return 'Shipping amount cannot be negative';
    return null;
};
const ManufacturerShipping = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [rates, setRates] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRunningBulkAction, setIsRunningBulkAction] = useState(false);
    const [availableManufacturers, setAvailableManufacturers] = useState([]);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editingRate, setEditingRate] = useState(null);
    const [editAmount, setEditAmount] = useState('0');
    const [editIsActive, setEditIsActive] = useState(true);
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [isDeletingEdit, setIsDeletingEdit] = useState(false);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [addName, setAddName] = useState('');
    const [addAmount, setAddAmount] = useState('0');
    const [isSavingAdd, setIsSavingAdd] = useState(false);
    const [addErrorMessage, setAddErrorMessage] = useState('');
    const latestRequestIdRef = useRef(0);
    const loadRates = useCallback(async (search) => {
        const requestId = ++latestRequestIdRef.current;
        try {
            setLoading(true);
            const response = await fetchManufacturerShippingRates({ search: search || undefined });
            if (requestId !== latestRequestIdRef.current)
                return;
            const data = response.body;
            const list = Array.isArray(data) ? data : data.results || [];
            const transformed = list.map((item) => ({
                id: item.id,
                name: item.name,
                shipping_amount: item.shippingAmount ?? item.shipping_amount ?? 0,
                is_active: toBooleanValue(item.isActive ?? item.is_active),
                created_at: item.createdAt ?? item.created_at,
                updated_at: item.updatedAt ?? item.updated_at,
            }));
            setRates(transformed);
        }
        catch (error) {
            if (requestId !== latestRequestIdRef.current)
                return;
            console.error('Error loading manufacturer shipping rates:', error);
            showToast({ message: 'Failed to load manufacturer shipping rates', variant: 'error', noBorder: true });
            setRates([]);
        }
        finally {
            if (requestId === latestRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [showToast]);
    const loadAvailableManufacturers = useCallback(async () => {
        try {
            const response = await fetchAvailableManufacturers();
            const list = response.body?.data ?? [];
            const transformed = list.map((item) => ({
                name: item.name,
                has_rate: toBooleanValue(item.hasRate ?? item.has_rate),
            }));
            setAvailableManufacturers(transformed);
        }
        catch (error) {
            // Degrade gracefully: the Typeahead falls back to plain free text below. No toast -
            // this is a nice-to-have picker, not a blocking failure.
            console.error('Error loading available manufacturers:', error);
            setAvailableManufacturers([]);
        }
    }, []);
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            loadRates(searchTerm);
        }, 300);
        return () => window.clearTimeout(timeoutId);
    }, [loadRates, searchTerm]);
    useEffect(() => {
        loadAvailableManufacturers();
    }, [loadAvailableManufacturers]);
    const handleBulkDelete = async (selectedRows) => {
        if (!selectedRows.length || isRunningBulkAction)
            return;
        const selectedIds = selectedRows.map((row) => row.id);
        if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected manufacturer shipping rate${selectedIds.length === 1 ? '' : 's'}?`)) {
            return;
        }
        setIsRunningBulkAction(true);
        try {
            const results = await Promise.allSettled(selectedIds.map((id) => deleteManufacturerShippingRate(id)));
            const successfulIds = [];
            const failedIds = [];
            results.forEach((result, index) => {
                const id = selectedIds[index];
                if (result.status === 'fulfilled') {
                    successfulIds.push(id);
                }
                else {
                    failedIds.push(id);
                }
            });
            if (successfulIds.length) {
                setRates((prev) => prev.filter((item) => !successfulIds.includes(item.id)));
                showToast({
                    message: `Deleted ${successfulIds.length} manufacturer shipping rate${successfulIds.length === 1 ? '' : 's'}`,
                    variant: failedIds.length ? 'warning' : 'success',
                    noBorder: true,
                });
                loadAvailableManufacturers();
            }
            if (failedIds.length) {
                console.error('Error deleting manufacturer shipping rates for IDs:', failedIds);
                showToast({
                    message: `Failed to delete ${failedIds.length} manufacturer shipping rate${failedIds.length === 1 ? '' : 's'}`,
                    variant: 'error',
                    noBorder: true,
                });
            }
        }
        catch (error) {
            console.error('Error deleting selected manufacturer shipping rates:', error);
            showToast({ message: 'Failed to delete selected manufacturer shipping rates', variant: 'error', noBorder: true });
        }
        finally {
            setIsRunningBulkAction(false);
        }
    };
    const handleOpenEdit = (rate) => {
        setEditingRate(rate);
        setEditAmount(String(rate.shipping_amount ?? 0));
        setEditIsActive(toBooleanValue(rate.is_active));
        setIsEditOpen(true);
    };
    const handleCloseEdit = () => {
        setIsEditOpen(false);
        setEditingRate(null);
        setEditAmount('0');
        setEditIsActive(true);
    };
    const handleOpenAdd = () => {
        setAddName('');
        setAddAmount('0');
        setAddErrorMessage('');
        setIsAddOpen(true);
    };
    const handleCloseAdd = () => {
        setIsAddOpen(false);
        setAddName('');
        setAddAmount('0');
        setAddErrorMessage('');
    };
    const handleSaveEdit = async () => {
        if (!editingRate)
            return;
        const validationMessage = validatePayload(editingRate.name, editAmount);
        if (validationMessage) {
            showToast({ message: validationMessage, variant: 'error', noBorder: true });
            return;
        }
        setIsSavingEdit(true);
        try {
            // The manufacturer name is intentionally read-only here (see the JSX below) -
            // renaming a rate keyed by a case-insensitively-unique name is really a
            // delete-and-create, so only the amount and active flag are ever patched.
            await updateManufacturerShippingRate(editingRate.id, {
                shipping_amount: toPriceValue(editAmount),
                is_active: editIsActive,
            });
            showToast({ message: 'Manufacturer shipping rate updated successfully', variant: 'success', noBorder: true });
            handleCloseEdit();
            loadRates(searchTerm);
        }
        catch (error) {
            console.error('Error updating manufacturer shipping rate:', error);
            showToast({ message: getRateErrorMessage(error), variant: 'error', noBorder: true });
        }
        finally {
            setIsSavingEdit(false);
        }
    };
    const handleDeleteEdit = async () => {
        if (!editingRate)
            return;
        if (!window.confirm(`Remove the shipping default for ${editingRate.name}?`)) {
            return;
        }
        setIsDeletingEdit(true);
        try {
            await deleteManufacturerShippingRate(editingRate.id);
            showToast({ message: 'Manufacturer shipping rate removed', variant: 'success', noBorder: true });
            handleCloseEdit();
            loadRates(searchTerm);
            loadAvailableManufacturers();
        }
        catch (error) {
            console.error('Error deleting manufacturer shipping rate:', error);
            showToast({ message: 'Failed to remove manufacturer shipping rate', variant: 'error', noBorder: true });
        }
        finally {
            setIsDeletingEdit(false);
        }
    };
    const handleSaveAdd = async () => {
        const validationMessage = validatePayload(addName, addAmount);
        if (validationMessage) {
            setAddErrorMessage(validationMessage);
            showToast({ message: validationMessage, variant: 'error', noBorder: true });
            return;
        }
        setIsSavingAdd(true);
        setAddErrorMessage('');
        try {
            await createManufacturerShippingRate({
                name: addName.trim(),
                shipping_amount: toPriceValue(addAmount),
            });
            showToast({ message: 'Manufacturer shipping rate created successfully', variant: 'success', noBorder: true });
            handleCloseAdd();
            loadRates(searchTerm);
            loadAvailableManufacturers();
        }
        catch (error) {
            console.error('Error creating manufacturer shipping rate:', error);
            const message = getRateErrorMessage(error);
            setAddErrorMessage(message);
            showToast({ message, variant: 'error', noBorder: true });
        }
        finally {
            setIsSavingAdd(false);
        }
    };
    const columns = createManufacturerShippingColumns();
    const bulkActions = [
        {
            label: 'Delete',
            action: (selectedRows) => handleBulkDelete(selectedRows),
        },
    ];
    const manufacturerSuggestions = availableManufacturers.map((entry) => ({
        id: entry.name,
        value: entry.name,
        label: entry.has_rate ? `${entry.name} (already configured)` : entry.name,
        disabled: entry.has_rate,
    }));
    const addNameMatchesKnownManufacturer = availableManufacturers.some((entry) => entry.name.trim().toLowerCase() === addName.trim().toLowerCase());
    return (_jsxs(AdminLayout, { children: [loading && _jsx("div", { children: "Loading..." }), _jsxs("div", { className: styles.tableWrapper, children: [_jsx("div", { className: styles.filterBar, children: _jsx("div", { className: styles.searchRow, children: _jsxs("div", { className: styles.searchInputWrapper, children: [_jsx(Input, { value: searchTerm, onChange: (value) => setSearchTerm(value || ''), placeholder: "Search by manufacturer name", width: "420px", showIcon: true, iconWidth: "16px", iconHeight: "16px" }), !!searchTerm && (_jsx("button", { type: "button", className: styles.clearButton, onClick: () => setSearchTerm(''), "aria-label": "Clear search", children: "x" }))] }) }) }), _jsx(SimpleTable, { columns: columns, data: rates, rowHeight: "60px", tableHeading: "Manufacturer Shipping", titleStyle: {
                            color: '#000',
                            fontFamily: 'Nunito',
                            fontSize: '24px',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            lineHeight: '28px',
                        }, enableSelection: true, hideTopBorder: true, enablePagination: true, paginationText: "Manufacturer rates per page", pageSize: 20, onRowClick: (item) => handleOpenEdit(item), bulkActions: bulkActions, customButton: _jsx(Button, { variant: "primaryV2", size: "small", label: "+ Add Manufacturer Shipping", onClick: handleOpenAdd }) })] }), isEditOpen && editingRate && (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.slideOutOverlay, onClick: handleCloseEdit }), _jsxs("div", { className: styles.slideOutPanel, children: [_jsx("div", { className: styles.slideOutHeader, children: _jsx("h2", { children: "Edit Manufacturer Shipping" }) }), _jsxs("div", { className: styles.slideOutContent, children: [_jsxs("div", { className: styles.filterField, children: [_jsx("label", { children: "Manufacturer" }), _jsx("p", { className: styles.readOnlyValue, children: editingRate.name })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "edit-manufacturer-shipping-amount", children: "Default Shipping Amount" }), _jsx("input", { id: "edit-manufacturer-shipping-amount", type: "number", min: "0", step: "0.01", value: editAmount, onChange: (event) => setEditAmount(event.target.value) })] }), _jsxs("label", { className: styles.checkboxField, children: [_jsx("input", { type: "checkbox", checked: editIsActive, onChange: (event) => setEditIsActive(event.target.checked) }), "Active"] })] }), _jsxs("div", { className: styles.slideOutFooter, children: [_jsx(Button, { variant: "secondaryV2", size: "small", label: isDeletingEdit ? 'Removing...' : 'Delete', onClick: handleDeleteEdit, disabled: isDeletingEdit || isSavingEdit }), _jsxs("div", { className: styles.slideOutFooterActions, children: [_jsx(Button, { variant: "secondaryV2", size: "small", label: "Cancel", onClick: handleCloseEdit }), _jsx(Button, { variant: "primaryV2", size: "small", label: isSavingEdit ? 'Saving...' : 'Save Changes', onClick: handleSaveEdit, disabled: isSavingEdit || isDeletingEdit })] })] })] })] })), isAddOpen && (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.slideOutOverlay, onClick: handleCloseAdd }), _jsxs("div", { className: styles.slideOutPanel, children: [_jsx("div", { className: styles.slideOutHeader, children: _jsx("h2", { children: "Add Manufacturer Shipping" }) }), _jsxs("div", { className: styles.slideOutContent, children: [!!addErrorMessage && (_jsx("div", { className: styles.inlineErrorBanner, role: "alert", children: addErrorMessage })), _jsx(Typeahead, { label: "Manufacturer", placeholder: "Search or type a manufacturer name", value: addName, suggestions: manufacturerSuggestions, valueTriggerLimit: 0, clearable: true, onChange: (event) => {
                                            setAddName(event.target.value);
                                            if (addErrorMessage)
                                                setAddErrorMessage('');
                                        }, onSelect: (suggestion) => setAddName(suggestion.value), invalid: !!addErrorMessage, inputWidth: "100%" }), !!addName.trim() && !addNameMatchesKnownManufacturer && (_jsx("p", { className: styles.fieldHint, children: "No products currently use this manufacturer name." })), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "add-manufacturer-shipping-amount", children: "Default Shipping Amount" }), _jsx("input", { id: "add-manufacturer-shipping-amount", type: "number", min: "0", step: "0.01", value: addAmount, onChange: (event) => {
                                                    setAddAmount(event.target.value);
                                                    if (addErrorMessage)
                                                        setAddErrorMessage('');
                                                } })] })] }), _jsxs("div", { className: styles.slideOutFooter, children: [_jsx("div", {}), _jsxs("div", { className: styles.slideOutFooterActions, children: [_jsx(Button, { variant: "secondaryV2", size: "small", label: "Cancel", onClick: handleCloseAdd }), _jsx(Button, { variant: "primaryV2", size: "small", label: isSavingAdd ? 'Saving...' : 'Create Rate', onClick: handleSaveAdd, disabled: isSavingAdd })] })] })] })] }))] }));
};
export default ManufacturerShipping;
